<template>
  <div v-if="booking && allocation" class="grow">
    <div class="w-fit">
      <ThresholdDatePicker
        :threshold-date="allocation.endDate"
        :initial-date="allocation.returnDate"
        :label="t('returnDate')"
        enable-time-picker
        @date-change="(val) => (returnDate = val)"
      />
    </div>
    <Divider />
    <ReturnCarStatusForm
      :booking="booking"
      :allocation="allocation"
      :is-saving="isSaving || isLoading"
      @save="handleSave"
    />
    <div v-if="allocation?.fuelLevelEnd !== null && allocation?.milageEnd !== null">
      <Divider />

      <Summary :booking="booking" :return-date="returnDate" />

      <Divider />

      <h2>{{ t('newDamages') }}</h2>
      <p v-for="(damage, i) in damages" :key="damage.id" class="mt-1">
        {{ `${i + 1}. ${damage.description}` }}
      </p>
      <div class="mt-2 flex items-center justify-end">
        <Information>
          <template #text>
            <div class="font-medium">
              {{ t('damagesInformationTitle') }}
            </div>
          </template>
          <template #modal>
            <div class="min-w-min">
              {{ t('damagesInformationText') }}
            </div>
          </template>
        </Information>
      </div>
      <Divider />
      <div class="flex justify-end">
        <ButtonNext
          :is-loading="isSaving"
          :disabled="
            isSaving ||
            typeof allocation.fuelLevelEnd !== 'number' ||
            typeof allocation.milageEnd !== 'number'
          "
          @click.prevent="handleContinue"
          >{{ t('next') }}</ButtonNext
        >
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useBooking, useUpdateBooking } from '@/queries/use-bookings';
import { useRoute } from 'vue-router';
import ReturnCarStatusForm from './ReturnCarStatusForm.vue';
import { useUpdateAllocation } from '@/queries/use-allocations';
import { useUpdateCar } from '@/queries/use-cars';
import { computed, ref } from 'vue';
import { useQueryClient } from '@tanstack/vue-query';
import Summary from './components/Summary.vue';
import { useDamages } from '@/queries/use-damages';
import { useI18n } from 'vue-i18n';
import Information from '@/components/Information.vue';
import ButtonNext from '@/components/buttons/ButtonNext.vue';
import Divider from '@/components/Divider.vue';
import { AllocationStatus } from '@carvia/ros-client-types';
import ThresholdDatePicker from '@/components/booking-forms/ThresholdDatePicker.vue';
import { useProcessNavigation } from '@/hooks/use-process-step-tracking';
import { useConfirmMilageDeviation } from '@/hooks/use-confirm-milage-deviation';

const { t } = useI18n();

const route = useRoute();
const { goToNext } = useProcessNavigation();
const queryClient = useQueryClient();
const { data: booking } = useBooking(route.params.id as string);
const allocation = computed(() => {
  return booking.value?.allocations[booking.value.allocations.length - 1];
});

const { confirmReturn, isLoading } = useConfirmMilageDeviation();
const { isPending: isSavingAllocation, mutateAsync: updateAllocation } = useUpdateAllocation();
const { mutateAsync: updateBooking, isPending: isSavingBooking } = useUpdateBooking();

const { mutateAsync: updateCar, isPending: isSavingCar } = useUpdateCar();
const isSaving = computed(() => isSavingAllocation.value || isSavingCar.value);

const { data: damages } = useDamages(
  { bookingIds: [route.params.id as string], withPosition: true },
  computed(() => !!booking.value),
);

const handleSave = async (values: { milage: number; fuelLevel: number }) => {
  if (!allocation.value || !booking.value) return;
  const { comment, isConfirmed, reason } = await confirmReturn(allocation.value, values.milage);
  if (!isConfirmed) {
    return;
  }
  await updateAllocation({
    id: allocation.value.id,
    allocation: {
      milageEnd: values.milage,
      fuelLevelEnd: values.fuelLevel,
      milageDeviationComment: comment,
      milageDeviationReason: reason,
    },
  });
  await queryClient.invalidateQueries({
    queryKey: ['bookings', booking.value.id],
  });
  await queryClient.invalidateQueries({
    queryKey: ['additional-costs', booking.value.id],
  });
  await updateCar({
    id: allocation.value.car.id,
    car: {
      fuelLevel: values.fuelLevel,
    },
  });
};
const returnDate = ref<string>();

const handleContinue = async () => {
  if (!allocation.value || !booking.value) return;
  if (returnDate.value) {
    await updateBooking({
      id: booking.value.id,
      booking: {
        returnDate: returnDate.value,
      },
    });
  }
  await updateAllocation({
    id: allocation.value.id,
    allocation: {
      returnDate: returnDate.value ? new Date(returnDate.value) : undefined,
      status: AllocationStatus.RETURNED,
    },
  });
  goToNext();
};
</script>

<i18n lang="json">
{
  "en": {
    "newDamages": "New Damages",
    "damagesInformationTitle": "Damages are calculated separately",
    "damagesInformationText": "Do not make any statement about the repair costs. The claims department will take care of it. Normally, a cost estimate or expert opinion is provided within 2 weeks. Deposit is retained.",
    "next": "Finish return and release deposit",
    "returnDate": "Return date"
  },
  "de": {
    "newDamages": "Neue Schäden",
    "damagesInformationTitle": "Schäden werden gesondert gerechnet",
    "damagesInformationText": "Keine Aussage zur Reparaturkosten machen. Schadensabteilung kümmert sich. Im Normalfall innerhalb von 2 Wochen Kostenvoranschlag oder Gutachten. Kaution wird einbehalten.",
    "next": "Rückgabe beenden und Kaution freigeben",
    "returnDate": "Rückgabedatum"
  },
  "es": {
    "newDamages": "Daños nuevos",
    "damagesInformationTitle": "Los daños se gestionan por separado",
    "damagesInformationText": "No haga ninguna declaración sobre el coste de la reparación. El departamento de siniestros se encarga. Normalmente se facilita un presupuesto o peritaje en un plazo de dos semanas. La fianza se retiene.",
    "next": "Finalizar devolución y liberar fianza",
    "returnDate": "Fecha de devolución"
  }
}
</i18n>
